import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import usePDIDSHook from "../hooks/usePDIDSHook";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const PurpleDoorIDSTable = () => {
  // Adjust this selector to match wherever your userAuth slice actually lives
  const authState = useSelector((state) => state.userAuth);

  const {
    creators,
    loading,
    error,
    searchCreators,
    registerCreator,
    registering,
    registerError,
    registerSuccess,
    setRegisterError,
    setRegisterSuccess,
  } = usePDIDSHook();

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ focus: "", lang: "", channelLink: "" });

  const filteredCreators = useMemo(() => creators, [creators]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    searchCreators(value);
  };

  const handleFormChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleJoinClick = (e) => {
    e.preventDefault();
    if (!authState?.user?._id) {
      setRegisterError("Please log in to register as a creator.");
      setShowModal(true);
      return;
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.focus.trim() || !form.lang.trim()) {
      setRegisterError("Focus area and language are required.");
      return;
    }

    const result = await registerCreator(
      {
        focus: form.focus.trim(),
        lang: form.lang.trim(),
        channelLink: form.channelLink.trim(),
      },
      authState?.accessToken
    );

    if (result.success) {
      setForm({ focus: "", lang: "", channelLink: "" });
      setTimeout(() => setShowModal(false), 1200);
    }
  };

  return (
    <>
      <section
        id="join"
        className="bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700 px-4 py-4 text-center md:py-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-xl"
        >
          <h2 className="text-cyan-200 text-2xl font-bold md:text-4xl">
            Become a Transformation Creator
          </h2>
          <p className="mt-2 text-sm text-teal-100 md:text-base">
            Create meaningful digital assets. Build long-term income. Leave a
            digital legacy.
          </p>
          <motion.button
            type="button"
            onClick={handleJoinClick}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="mt-4 inline-block rounded-full bg-blue-600 px-2 py-1 text-xs lg:text-sm font-semibold text-gray-100 shadow-xl md:px-8 md:py-3 md:text-base"
          >
            Create Your Content for Think5Whys, Publish on YouTube and
            Register Your Video Here!
          </motion.button>
        </motion.div>
      </section>

      <section id="creators" className="px-4 py-2 md:px-4 md:py-2">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-6 text-center"
          >
            <h2 className="text-cyan-200 text-2xl font-bold md:text-3xl">
              Featured Transformation Creators
            </h2>
            <p className="mt-2 text-sm text-zinc-200">
              Real people building meaningful channels
            </p>
          </motion.div>

          <input
            type="text"
            placeholder="Search by name, focus or language..."
            value={search}
            onChange={handleSearchChange}
            className="mb-5 w-full rounded-full border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-sm text-zinc-200 outline-none placeholder:text-zinc-200 focus:border-teal-500 md:max-w-md"
          />

          {loading && (
            <p className="mb-4 text-center text-sm text-zinc-200">
              Loading creators…
            </p>
          )}
          {!loading && error && (
            <p className="mb-4 text-center text-sm text-red-400">{error}</p>
          )}
          {!loading && !error && filteredCreators.length === 0 && (
            <p className="mb-4 text-center text-sm text-zinc-200">
              No creators found.
            </p>
          )}

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filteredCreators.map((c) => (
              <div
                key={c._id}
                className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-3"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-xs font-bold text-white">
                  {c.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">
                    {c.name}
                  </div>
                  <div className="truncate text-[11px] text-zinc-200">
                    {c.focus} · {c.lang}
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-200">
                    {c.subs} subs · ★ {c.rating}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-2xl border border-zinc-800 md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-900 text-zinc-200">
                <tr>
                  <th className="px-4 py-3 font-medium">Creator</th>
                  <th className="px-4 py-3 font-medium">Focus</th>
                  <th className="px-4 py-3 font-medium">Language</th>
                  <th className="px-4 py-3 font-medium">Subs</th>
                  <th className="px-4 py-3 font-medium">Videos</th>
                  <th className="px-4 py-3 font-medium">Avg Views</th>
                  <th className="px-4 py-3 font-medium">Rating</th>
                </tr>
              </thead>
              <tbody>
                {filteredCreators.map((c) => (
                  <tr
                    key={c._id}
                    className="border-t border-zinc-800 hover:bg-zinc-900/50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-[10px] font-bold text-white">
                          {c.initials}
                        </div>
                        <span className="font-medium">{c.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-zinc-200">{c.focus}</td>
                    <td className="px-4 py-3 text-zinc-200">{c.lang}</td>
                    <td className="px-4 py-3">{c.subs}</td>
                    <td className="px-4 py-3">{c.videos}</td>
                    <td className="px-4 py-3">{c.views}</td>
                    <td className="px-4 py-3 text-amber-400">★ {c.rating}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ───── Registration modal (functionality only — matches existing palette) ───── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6"
          >
            <h3 className="text-lg font-bold text-cyan-200">
              Register as a Creator
            </h3>
            <p className="mt-1 text-xs text-zinc-200">
              We'll link this to your Think5Whys profile (
              {authState?.user?.name || "your account"}).
            </p>

            {!authState?.user?._id ? (
              <div className="mt-4">
                <p className="text-sm text-red-400">
                  {registerError || "Please log in to continue."}
                </p>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="mt-4 w-full rounded-full border border-zinc-700 py-2 text-sm text-zinc-200"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-4 space-y-3">
                <input
                  type="text"
                  name="focus"
                  placeholder="Content focus (e.g., Mindset, Health)"
                  value={form.focus}
                  onChange={handleFormChange}
                  className="w-full rounded-full border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-sm text-zinc-200 outline-none focus:border-teal-500"
                />
                <input
                  type="text"
                  name="lang"
                  placeholder="Primary language"
                  value={form.lang}
                  onChange={handleFormChange}
                  className="w-full rounded-full border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-sm text-zinc-200 outline-none focus:border-teal-500"
                />
                <input
                  type="url"
                  name="channelLink"
                  placeholder="YouTube channel link"
                  value={form.channelLink}
                  onChange={handleFormChange}
                  className="w-full rounded-full border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-sm text-zinc-200 outline-none focus:border-teal-500"
                />

                {registerError && (
                  <p className="text-xs text-red-400">{registerError}</p>
                )}
                {registerSuccess && (
                  <p className="text-xs text-teal-300">{registerSuccess}</p>
                )}

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setRegisterError("");
                      setRegisterSuccess("");
                    }}
                    className="w-1/2 rounded-full border border-zinc-700 py-2 text-sm text-zinc-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={registering}
                    className="w-1/2 rounded-full bg-white py-2 text-sm font-semibold text-teal-800 disabled:opacity-60"
                  >
                    {registering ? "Submitting…" : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </>
  );
};

export default PurpleDoorIDSTable;
